import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChatAttachments } from "./hooks";
import { EmptyStateView, ConversationView, AgentView, ChatSkeleton } from "./components";
import { useHomeChat } from "../../context";
import { useToast } from "../../../../../components/toast";
import SaveToDriveModal from "../SaveToDriveModal/SaveToDriveModal";
import { useDeleteDocument } from "../../hooks/useDeleteDocument";
import DeleteDocumentModal from "../DocumentPanel/DeleteDocumentModal";
import DrivePickerModal from "../DrivePickerModal";
import { generatePdf, generateDocx } from "../../../../../components/DocumentCanvas/DocumentUtils";
import { useSaveDocumentToDrive } from "../../hooks/useSaveDocumentToDrive/useSaveDocumentToDrive";

const ADD_FROM_DRIVE_VALUES = ["drive", "addFromDrive"];
const SAVE_TO_DRIVE_VALUES = ["save_to_drive", "saveToDrive", "drive"];
const GO_TO_DRIVE_VALUES = ["go_to_drive", "goToDrive", "goto_drive"];
const DOWNLOAD_PDF_VALUES = ["pdf", "download_pdf", "downloadPdf", "download"];
const DOWNLOAD_DOCX_VALUES = ["docx", "download_docx", "downloadDocx"];

const ChatPanel = () => {
  const {
    selectedConversationId,
    selectedAgentId,
    messages,
    isLoadingMessages,
    fetchMessages,
    createNewConversation,
    sortedDocuments,
    openDocumentPanel,
    getAgentById,
    getDocument,
    updateDocument,
    userId,
    tenantId,
  } = useHomeChat();
  const toast = useToast();
  const { handleDeleteDocument } = useDeleteDocument();
  const navigate = useNavigate();
  const [saveToDriveDocId, setSaveToDriveDocId] = useState(null);
  const [showSaveToDriveModal, setShowSaveToDriveModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showDrivePickerModal, setShowDrivePickerModal] = useState(false);

  const { saveToVault, isSaving } = useSaveDocumentToDrive({
    getContent: () => {
      const doc = saveToDriveDocId ? getDocument(saveToDriveDocId) : null;
      const content = doc?.content ?? "";
      return typeof content === "string" ? content : { toText: () => String(content ?? "") };
    },
    getTitle: () => {
      const doc = saveToDriveDocId ? getDocument(saveToDriveDocId) : null;
      return doc?.title ?? "Document";
    },
    getAgentInfo: () => ({ agentUuid: selectedAgentId }),
    getAuthContext: () => ({ userId, tenantId }),
    onSuccess: (message) => toast.success({ message }),
    onError: (error) => toast.error({ message: error?.message ?? "Failed to save document." }),
  });

  const handleDocumentSearchMenuChange = useCallback(
    (value, doc) => {
      if (SAVE_TO_DRIVE_VALUES.includes(value)) {
        setSaveToDriveDocId(doc?.id ?? null);
        setShowSaveToDriveModal(true);
      } else if (GO_TO_DRIVE_VALUES.includes(value) && doc?.driveUuid) {
        navigate(`/v2/drives/d/${doc.driveUuid}`);
      } else if (DOWNLOAD_PDF_VALUES.includes(value) || DOWNLOAD_DOCX_VALUES.includes(value)) {
        const fullDoc = doc?.id ? getDocument(doc.id) : null;
        if (!fullDoc) {
          toast.error({ message: "Document not found." });
          return;
        }
        const content = fullDoc.content ?? "";
        const isEmpty = !content?.trim() || content === "No content available";
        if (isEmpty) {
          toast.error({ message: "Document is empty." });
          return;
        }
        const rawTitle = fullDoc.title ?? "Document";
        const title = rawTitle.replace(/\s+/g, "_").trim() || "Document";
        const stateToUse = { toText: () => content };
        const promise =
          DOWNLOAD_PDF_VALUES.includes(value)
            ? generatePdf(stateToUse, title)
            : generateDocx(stateToUse, title);
        promise.catch((err) => {
          toast.error({
            message: err?.message ?? `Failed to generate ${DOWNLOAD_PDF_VALUES.includes(value) ? "PDF" : "DOCX"}.`,
          });
        });
      }
    },
    [navigate, getDocument, toast]
  );

  const mappedDocuments = useMemo(
    () =>
      sortedDocuments.map((d) => {
        const preview = d.preview || "";
        const shortDesc =
          preview.length > 40 ? `${preview.substring(0, 40)}...` : preview;
        return {
          id: d.id,
          title: d.title,
          description: shortDesc,
          agentName: d.agentUuid ? (getAgentById(d.agentUuid)?.name || "—") : "",
          driveName: d.driveUuid ? d.vaultName ?? "Drive" : "",
          driveUuid: d.driveUuid ?? null,
        };
      }),
    [sortedDocuments, getAgentById]
  );

  const handleDocumentSelect = useCallback(
    (doc) => {
      openDocumentPanel({ documentId: doc.id });
    },
    [openDocumentPanel]
  );

  const handleDocumentDeleteClick = useCallback((docOrId) => {
    const doc =
      typeof docOrId === "object"
        ? docOrId
        : getDocument(docOrId) ?? { id: docOrId, title: "" };
    if (doc?.id) setDocumentToDelete(doc);
  }, [getDocument]);

  const handleCloseDeleteModal = useCallback(() => setDocumentToDelete(null), []);

  const handleConfirmDeleteDocument = useCallback(
    async () => {
      const id = documentToDelete?.id;
      if (!id) {
        setDocumentToDelete(null);
        return;
      }
      try {
        const fullDoc = getDocument(id) ?? documentToDelete;
        await handleDeleteDocument(fullDoc);
      } catch {
        // handleDeleteDocument already shows toast on error
      } finally {
        setDocumentToDelete(null);
      }
    },
    [documentToDelete, getDocument, handleDeleteDocument]
  );

  const {
    attachments,
    handleFilesSelected,
    handleAttachmentRemove,
    handleAddDocumentsFromDrive,
    clearAttachments,
    handleAddMenuChange,
    handleModeMenuChange,
  } = useChatAttachments({
    agentUuid: selectedAgentId,
    conversationUuid: selectedConversationId,
    createNewConversation,
  });

  const handleAddMenuChangeWithDrive = useCallback(
    (value, itemData) => {
      if (ADD_FROM_DRIVE_VALUES.includes(value)) {
        setShowDrivePickerModal(true);
      } else {
        handleAddMenuChange?.(value, itemData);
      }
    },
    [handleAddMenuChange]
  );

  const handleCloseDrivePickerModal = useCallback(
    () => setShowDrivePickerModal(false),
    []
  );

  const handleDrivePickerConfirm = useCallback(
    (selectedDocuments) => {
      if (selectedDocuments?.length) {
        handleAddDocumentsFromDrive(selectedDocuments);
      }
      setShowDrivePickerModal(false);
    },
    [handleAddDocumentsFromDrive]
  );

  const handleModeTagClose = useCallback(
    (modeValue) => {
      if (ADD_FROM_DRIVE_VALUES.includes(modeValue)) {
        clearAttachments();
      }
    },
    [clearAttachments]
  );

  // Fetch messages when conversation is selected. Skip-if-already-loaded lives in Provider.
  useEffect(() => {
    if (selectedConversationId) {
      fetchMessages(selectedConversationId);
    }
  }, [selectedConversationId, fetchMessages]);

  const showEmptyState = !selectedConversationId && !selectedAgentId;
  // While loading a conversation, show skeleton so we don't flash AgentView then ConversationView
  const showConversationSkeleton = selectedConversationId && isLoadingMessages;
  // Conversation view when we have messages (or finished loading with empty thread)
  const showConversationView =
    selectedConversationId && !isLoadingMessages && messages.length > 0;
  // Empty conversation (selected but no messages after load)
  const showConversationEmpty =
    selectedConversationId && !isLoadingMessages && messages.length === 0;
  // Agent view: agent selected and no conversation, or conversation empty after load
  const showAgentView = selectedAgentId && !selectedConversationId;

  const docToSave = saveToDriveDocId ? getDocument(saveToDriveDocId) : null;

  const commonDocProps = useMemo(
    () => ({
      documents: mappedDocuments,
      onDocumentSelect: handleDocumentSelect,
      onDocumentDelete: handleDocumentDeleteClick,
      onDocumentSearchMenuChange: handleDocumentSearchMenuChange,
    }),
    [mappedDocuments, handleDocumentSelect, handleDocumentDeleteClick, handleDocumentSearchMenuChange]
  );

  const handleCloseSaveToDriveModal = useCallback(() => {
    setShowSaveToDriveModal(false);
    setSaveToDriveDocId(null);
  }, []);

  const handleSaveToDrive = useCallback(
    async (fileName, fileType, destinations) => {
      const result = await saveToVault(fileName, fileType, destinations);
      if (result && saveToDriveDocId && updateDocument) {
        updateDocument(saveToDriveDocId, {
          driveUuid: result.vaultUuid,
          vaultName: result.vaultName,
          title: result.fileName ?? fileName,
        });
      }
      setShowSaveToDriveModal(false);
      setSaveToDriveDocId(null);
    },
    [saveToVault, saveToDriveDocId, updateDocument]
  );

  let content = null;
  if (showEmptyState) {
    content = (
      <EmptyStateView
        attachments={attachments}
        onFilesSelected={handleFilesSelected}
        onAttachmentRemove={handleAttachmentRemove}
        onClearAttachments={clearAttachments}
        onAddMenuChange={handleAddMenuChangeWithDrive}
        onModeMenuChange={handleModeMenuChange}
        onModeTagClose={handleModeTagClose}
        {...commonDocProps}
      />
    );
  } else if (showConversationSkeleton) {
    content = <ChatSkeleton show />;
  } else if (showConversationView) {
    content = (
      <ConversationView
        messages={messages}
        attachments={attachments}
        onFilesSelected={handleFilesSelected}
        onAttachmentRemove={handleAttachmentRemove}
        onClearAttachments={clearAttachments}
        onAddMenuChange={handleAddMenuChangeWithDrive}
        onModeTagClose={handleModeTagClose}
        onModeMenuChange={handleModeMenuChange}
        clearAttachments={clearAttachments}
        isLoadingMessages={isLoadingMessages}
        {...commonDocProps}
      />
    );
  } else if (showAgentView || showConversationEmpty) {
    content = (
      <AgentView
        selectedAgentId={selectedAgentId}
        attachments={attachments}
        onFilesSelected={handleFilesSelected}
        onAttachmentRemove={handleAttachmentRemove}
        onClearAttachments={clearAttachments}
        onAddMenuChange={handleAddMenuChangeWithDrive}
        onModeTagClose={handleModeTagClose}
        onModeMenuChange={handleModeMenuChange}
        clearAttachments={clearAttachments}
        isLoadingMessages={isLoadingMessages}
        {...commonDocProps}
      />
    );
  }

  return (
    <>
      {content}
      <SaveToDriveModal
        isOpen={showSaveToDriveModal}
        onClose={handleCloseSaveToDriveModal}
        documentTitle={docToSave?.title ?? "Document"}
        onSave={handleSaveToDrive}
      />
      <DeleteDocumentModal
        isOpen={!!documentToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDeleteDocument}
        documentTitle={documentToDelete?.title ?? ""}
      />
      <DrivePickerModal
        isOpen={showDrivePickerModal}
        onClose={handleCloseDrivePickerModal}
        onConfirm={handleDrivePickerConfirm}
      />
    </>
  );
};

export default ChatPanel;
